import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import { CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { useMutation } from "@apollo/client";
import {
  CREATE_ONBOARDING_FORM,
  UPDATE_ONBOARDING_FORM_PARTIAL,
} from "../../graphQl/Queries.js/OnboardingQueries";
import { aboutfox } from "../../assets/images";

const darkprimary = "#003959";

const textFieldSX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f8fafc",
    borderRadius: "12px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: darkprimary },
    "&.Mui-focused fieldset": { borderColor: darkprimary, borderWidth: "2px" },
    "& input": {
      color: darkprimary,
      padding: "16px 16px",
      fontFamily: "Poppins, sans-serif",
      fontSize: "15px",
    },
    "& .MuiSelect-select": {
      padding: "16px 16px",
      fontFamily: "Poppins, sans-serif",
      fontSize: "15px",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#94a3b8",
    fontFamily: "Poppins, sans-serif",
    fontSize: "15px",
  },
  "& .MuiInputLabel-root.Mui-focused": { color: darkprimary },
};

const StepDots = ({ current, total }) => (
  <div className="flex items-center justify-center gap-2 mb-5">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`h-2 rounded-full transition-all duration-300 ${
          i + 1 === current
            ? "w-8 bg-darkprimary"
            : i + 1 < current
            ? "w-2 bg-darkprimary"
            : "w-2 bg-gray-300"
        }`}
      />
    ))}
  </div>
);

const HeroForm = () => {
  const [createOnboardingForm] = useMutation(CREATE_ONBOARDING_FORM);
  const [updateOnboardingFormPartial] = useMutation(UPDATE_ONBOARDING_FORM_PARTIAL);

  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingFormId, setOnboardingFormId] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);


  const [formData, setFormData] = useState({
    guardianName: "",
    guardianEmail: "",
    phoneNumber: "",
    contactPreference: "Phone",
    studentFirstName: "",
    studentLastName: "",
    studentAge: "",
    studentEmail: "",
    otherInterest: "",
    scholarshipSelect: "No Scholarship",
    scholarshipOther: "",
  });

  const availableInterests = [
    "Gaming",
    "Coding",
    "Game Design",
    "Roblox Studio",
    "Roblox Scripting",
    "Minecraft Modding",
    "Drawing",
    "Art",
    "Programming",
    "Other",
  ];
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [userTimeZone, setUserTimeZone] = useState("");
  const [currentUrl, setCurrentUrl] = useState("");

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Denver";
    setUserTimeZone(tz);
    setCurrentUrl(window.location.href);
  }, []);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const formatPhoneNumber = (val) => {
    const numbers = val.replace(/\D/g, "");
    const char = { 0: "(", 3: ") ", 6: "-" };
    return numbers
      .substring(0, 10)
      .split("")
      .map((n, i) => (char[i] || "") + n)
      .join("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: formatPhoneNumber(value) }));
  };

  const toggleInterest = (interest) => {
    if (interest === "Other") {
      if (!selectedInterests.includes(interest)) {
        setSelectedInterests([...selectedInterests, interest]);
      }
      return;
    }
    setSelectedInterests((current) =>
      current.includes(interest)
        ? current.filter((item) => item !== interest)
        : [...current, interest]
    );
  };

  const handleScholarshipSelectChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      scholarshipSelect: value,
      scholarshipOther: value === "Other" ? prev.scholarshipOther : "",
    }));
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setSubmissionError(null);
    if (!isValidEmail(formData.guardianEmail)) {
      setSubmissionError({ message: "Please enter a valid email address." });
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await createOnboardingForm({
        variables: {
          input: {
            guardianName: formData.guardianName,
            guardianEmail: formData.guardianEmail,
            phoneNumber: formData.phoneNumber,
            contactPreference: formData.contactPreference,
            submittedFromURL: currentUrl,
            userTimeZone,
          },
        },
      });
      setOnboardingFormId(res.data.createOnboardingForm.id);
      setCurrentStep(2);
    } catch (err) {
      setSubmissionError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setSubmissionError(null);
    if (!formData.studentFirstName || !formData.studentLastName || !formData.studentAge || !formData.studentEmail) {
      setSubmissionError({ message: "Please fill in all fields." });
      return;
    }
    if (!isValidEmail(formData.studentEmail)) {
      setSubmissionError({ message: "Please enter a valid student email." });
      return;
    }
    setIsSubmitting(true);
    try {
      await updateOnboardingFormPartial({
        variables: {
          id: onboardingFormId,
          input: {
            studentFirstName: formData.studentFirstName,
            studentLastName: formData.studentLastName,
            studentAge: formData.studentAge,
            studentEmail: formData.studentEmail,
          },
        },
      });
      setCurrentStep(3);
    } catch (err) {
      setSubmissionError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStep3Submit = async (e) => {
    e.preventDefault();
    setSubmissionError(null);
    const baseInterests = selectedInterests.filter((i) => i !== "Other").join(", ");
    const studentInterest =
      selectedInterests.includes("Other") && formData.otherInterest
        ? `${baseInterests}, ${formData.otherInterest}`
        : baseInterests;
    setIsSubmitting(true);
    try {
      await updateOnboardingFormPartial({
        variables: {
          id: onboardingFormId,
          input: { studentInterest, otherInterest: formData.otherInterest },
        },
      });
      setCurrentStep(4);
    } catch (err) {
      setSubmissionError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStep4Submit = async (e) => {
    e.preventDefault();
    setSubmissionError(null);
    const scholarshipName =
      formData.scholarshipSelect === "Other"
        ? formData.scholarshipOther.trim()
        : formData.scholarshipSelect;
    setIsSubmitting(true);
    try {
      await updateOnboardingFormPartial({
        variables: {
          id: onboardingFormId,
          input: { scholarshipName, scholarshipOther: formData.scholarshipOther },
        },
      });
      setIsSubmitted(true);
    } catch (err) {
      setSubmissionError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const SubmitButton = ({ children }) => (
    <button
      type="submit"
      disabled={isSubmitting}
      className="w-full bg-darkprimary hover:bg-darkprimary/90 text-white font-nunito font-bold text-lg py-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 cursor-pointer shadow-md hover:shadow-lg"
    >
      {isSubmitting ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <>
          {children}
          <ArrowRight className="w-5 h-5" />
        </>
      )}
    </button>
  );

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 text-center fade-in">
        <div className="w-16 h-16 bg-darkprimary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-darkprimary" />
        </div>
        <h3 className="font-nunito text-2xl font-bold text-darkprimary mb-2">
          You're All Set!
        </h3>
        <p className="font-poppins text-darkprimary/70 mb-4">
          We'll reach out shortly to schedule your child's free trial class.
        </p>
        <img src={aboutfox} className="w-32 mx-auto" alt="Tech Tails Fox" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl">
      {/* Header */}
      <div className="bg-darkprimary px-6 py-4 rounded-t-2xl">
        <h3 className="font-nunito text-white font-bold text-lg text-center">
          {currentStep === 1 && "Claim Your Free Trial Class"}
          {currentStep === 2 && "Tell Us About Your Student"}
          {currentStep === 3 && "Personalize Their Experience"}
          {currentStep === 4 && "One Last Thing"}
        </h3>
        {currentStep === 1 && (
          <p className="text-white/60 text-sm text-center font-poppins mt-1">
            45 minutes, live, one-on-one, completely free
          </p>
        )}
      </div>

      <div className="p-6">
        {/* Only show step dots after step 1 */}
        {currentStep > 1 && <StepDots current={currentStep} total={4} />}

        {submissionError && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2">
            {submissionError.message || "Something went wrong. Please try again."}
          </div>
        )}

        <div className="fade-in" key={currentStep}>
          {/* STEP 1 */}
          {currentStep === 1 && (
            <form onSubmit={handleStep1Submit} className="flex flex-col gap-4">
              <TextField
                label="Your Full Name"
                name="guardianName"
                value={formData.guardianName}
                onChange={handleChange}
                required
                fullWidth
                sx={textFieldSX}
              />
              <TextField
                label="Email Address"
                name="guardianEmail"
                value={formData.guardianEmail}
                onChange={handleChange}
                required
                fullWidth
                sx={textFieldSX}
              />
              <TextField
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handlePhoneChange}
                required
                fullWidth
                sx={textFieldSX}
                inputProps={{ maxLength: 14 }}
              />
              <TextField
                select
                label="Contact Preference"
                name="contactPreference"
                value={formData.contactPreference}
                onChange={handleChange}
                fullWidth
                sx={textFieldSX}
                SelectProps={{ native: true }}
              >
                <option value="Phone">Phone</option>
                <option value="Email">Email</option>
              </TextField>
              <SubmitButton>Get My Free Class</SubmitButton>
              <p className="text-xs text-center text-gray-400 font-poppins">
                No payment required. By submitting you agree to our{" "}
                <a href="/pages/termsofservice" className="text-pinkprimary hover:underline">
                  Terms of Service
                </a>
                .
              </p>
            </form>
          )}

          {/* STEP 2 */}
          {currentStep === 2 && (
            <form onSubmit={handleStep2Submit} className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  label="First Name"
                  name="studentFirstName"
                  value={formData.studentFirstName}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={textFieldSX}
                />
                <TextField
                  label="Last Name"
                  name="studentLastName"
                  value={formData.studentLastName}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={textFieldSX}
                />
              </div>
              <TextField
                label="Student Age"
                name="studentAge"
                value={formData.studentAge}
                onChange={handleChange}
                required
                fullWidth
                sx={textFieldSX}
              />
              <TextField
                label="Student Email"
                name="studentEmail"
                value={formData.studentEmail}
                onChange={handleChange}
                required
                fullWidth
                sx={textFieldSX}
                helperText="We'll send class details to this email."
                FormHelperTextProps={{ sx: { fontFamily: "Poppins, sans-serif", fontSize: "12px", color: "#94a3b8" } }}
              />
              <SubmitButton>Continue</SubmitButton>
            </form>
          )}

          {/* STEP 3 */}
          {currentStep === 3 && (
            <form onSubmit={handleStep3Submit} className="flex flex-col gap-4">
              <p className="text-sm text-gray-500 font-poppins">
                What is your student interested in? This helps us personalize the class.
              </p>
              <div className="flex flex-wrap gap-2">
                {availableInterests.map((interest) => (
                  <button
                    key={interest}
                    type="button"
                    onClick={() => toggleInterest(interest)}
                    className={`px-3 py-2 rounded-full text-sm font-medium border-2 transition-all cursor-pointer ${
                      selectedInterests.includes(interest)
                        ? "bg-darkprimary border-darkprimary text-white"
                        : "bg-gray-50 border-gray-200 text-gray-600 hover:border-darkprimary"
                    }`}
                  >
                    {interest}
                  </button>
                ))}
              </div>
              {selectedInterests.includes("Other") && (
                <TextField
                  label="Other Interest"
                  name="otherInterest"
                  value={formData.otherInterest}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={textFieldSX}
                />
              )}
              <SubmitButton>Continue</SubmitButton>
            </form>
          )}

          {/* STEP 4 */}
          {currentStep === 4 && (
            <form onSubmit={handleStep4Submit} className="flex flex-col gap-4">
              <p className="text-sm text-gray-500 font-poppins">
                Using a scholarship? Let us know so we can help with the process.
              </p>
              <TextField
                select
                label="Scholarship"
                name="scholarshipSelect"
                value={formData.scholarshipSelect}
                onChange={handleScholarshipSelectChange}
                fullWidth
                sx={textFieldSX}
                SelectProps={{ native: true }}
              >
                <option value="No Scholarship">No Scholarship</option>
                <option value="Utah Fits All">Utah Fits All</option>
                <option value="OpenEd">OpenEd</option>
                <option value="ESA Arizona">ESA Arizona</option>
                <option value="Step Up for Students">Step Up for Students</option>
                <option value="Other">Other</option>
              </TextField>
              {formData.scholarshipSelect === "Other" && (
                <TextField
                  label="Scholarship Name"
                  name="scholarshipOther"
                  value={formData.scholarshipOther}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={textFieldSX}
                />
              )}
              <SubmitButton>Claim Free Trial</SubmitButton>
              <p className="text-xs text-center text-gray-400 font-poppins">
                By finishing, you agree to our{" "}
                <a href="/pages/termsofservice" className="text-pinkprimary hover:underline">
                  Terms of Service
                </a>
                .
              </p>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .fade-in {
          animation: fadeIn 0.4s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default HeroForm;
